'use client'

import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { useLanguage } from '@/contexts/LanguageContext'
import { testimonialsTranslations } from '@/translations/testimonials'
import { Quote, Star, ExternalLink } from 'lucide-react'

export default function Testimonials() {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  })

  const { language } = useLanguage()
  const t = testimonialsTranslations[language]

  return (
    <section id="testimonials" className="py-16 bg-gradient-to-br from-white to-gray-50/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center">
        {/* Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
            {t.title}
          </h2>
          <p className="text-large text-gray-600 max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </motion.div>

        {/* Google Reviews Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="w-full max-w-2xl mb-12"
        >
          <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
            <div className="text-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                {t.googleReviews.title}
              </h3>
              <div className="flex items-center justify-center gap-3 mb-3">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-4 h-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {t.googleReviews.rating}
                </span>
                <span className="text-gray-500">•</span>
                <span className="text-sm text-gray-600">
                  {t.googleReviews.reviews}
                </span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="https://www.google.com/search?q=JC+Gardening+Barry+reviews"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 transition-colors"
              >
                {t.googleReviews.cta}
                <ExternalLink className="w-3 h-3" />
              </a>
              <a
                href="https://g.page/r/CVKSNr27CjznEAE/review"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
              >
                {t.googleReviews.leaveReview}
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </motion.div>

        {/* Testimonials */}
        <div className="grid lg:grid-cols-3 gap-8 justify-center">
          {t.testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
            >
              {/* Opening Quote Icon */}
              <div className="mb-2">
                <Quote className="w-4 h-4 text-gray-800 fill-current" />
              </div>

              {/* Testimonial Content */}
              <div className="mb-6">
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  {testimonial.content}
                </p>
              </div>

              {/* Author Info */}
              <div className="flex items-center">
                <div>
                  <div className="font-semibold text-gray-900 text-lg">{testimonial.author}</div>
                  <p className="text-gray-600 text-sm mb-0">{testimonial.role}</p>
                  <div className="text-sm text-gray-600">{testimonial.location}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Social Proof */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-16 max-w-md mx-auto"
        >
          <p className="text-lg text-gray-600">
            {t.socialProof}
          </p>
        </motion.div>
      </div>
    </section>
  )
} 